import { makeArticle, notWorkingRelated } from "./learnFactory";

// NOTE: pageTiredOfFeelingDepressed is exported from learnDataTiredOfFeelingDepressed.tsx
// with slug "tired-of-feeling-depressed-options-left" to avoid duplicate slug conflicts.
// Do not re-declare it here.

export const pageNothingWorksForDepression = makeArticle({
  slug: "nothing-works-for-my-depression",
  title: "Nothing Works for My Depression — What Now? | Neuro Wellness",
  description:
    "If you feel like nothing works for your depression, there are treatments you may not have tried. Learn about TMS, SAINT®, SPRAVATO®, and next steps.",
  canonical: "/learn/nothing-works-for-my-depression",
  h1: "I Feel Like Nothing Works for My Depression",
  breadcrumbLabel: "Nothing Works",
  shortAnswer:
    "If you feel like nothing works for your depression, you are not alone, and there are treatments you may not have tried. When multiple antidepressants and therapy haven't provided adequate relief, treatments like TMS, SAINT® accelerated TMS, SPRAVATO® (esketamine), and ECT work through different mechanisms and are specifically indicated for treatment-resistant depression. The key is a thorough psychiatric evaluation to review your treatment history, confirm past trials were adequate, and identify the right next step. If you are having thoughts of self-harm, call or text 988 now.",
  keyTakeaways: [
    "Feeling like nothing works is common and understandable.",
    "TMS, SAINT®, SPRAVATO®, and ECT work differently than medication.",
    "A psychiatric evaluation reviews your full treatment history.",
    "Past trials may not have been adequate (dose, duration).",
    "If having thoughts of self-harm, call 988.",
  ],
  sections: [
    {
      heading: "Why It Feels Like Nothing Works",
      paragraphs: [
        "When multiple treatments haven't provided relief, it can feel hopeless. But there are several reasons this may happen:",
      ],
      list: [
        "Past medication trials may not have been adequate (dose too low, duration too short)",
        "An underlying condition (bipolar disorder, thyroid issues, substance use) may be missed",
        "The treatments tried may all work through the same mechanism",
        "Treatments like TMS and SAINT® work through entirely different mechanisms than medication",
      ],
    },
    {
      heading: "Treatments That Work Differently",
      paragraphs: [
        "If antidepressants haven't worked, treatments that stimulate the brain directly — without medication — may help:",
      ],
      list: [
        "TMS — FDA-cleared magnetic brain stimulation, no medication",
        "SAINT® — FDA-cleared accelerated 5-day fMRI-guided TMS",
        "SPRAVATO® — FDA-approved esketamine nasal spray, different mechanism than standard antidepressants",
        "ECT — for severe or psychotic depression",
      ],
    },
    {
      heading: "The Importance of a Thorough Evaluation",
      paragraphs: [
        "A psychiatric evaluation reviews your full treatment history, confirms whether past trials were adequate, screens for missed conditions, and identifies which treatment may be appropriate. Sometimes the issue isn't that nothing works — it's that the right treatment hasn't been tried yet. If you are having thoughts of self-harm, call or text 988 (Suicide & Crisis Lifeline) now.",
      ],
    },
  ],
  faqs: [
    {
      question: "What if nothing works for my depression?",
      answer:
        "There are treatments you may not have tried. TMS, SAINT®, SPRAVATO®, and ECT work through different mechanisms than medication and are indicated for treatment-resistant depression. A psychiatric evaluation can identify the right next step. If having thoughts of self-harm, call 988.",
    },
    {
      question: "Why do I feel like nothing works for my depression?",
      answer:
        "Past medication trials may not have been adequate, an underlying condition may be missed, or all treatments tried may work through the same mechanism. Treatments like TMS and SAINT® work differently. A thorough evaluation can identify the issue.",
    },
    {
      question: "Is there hope when depression feels impossible to treat?",
      answer:
        "Yes. Treatments like TMS, SAINT®, SPRAVATO®, and ECT were developed for depression that hasn't responded to other treatments. A psychiatric evaluation can review your history and identify options you haven't tried.",
    },
  ],
  sources: [
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],
  relatedLinks: notWorkingRelated,
});

export const pageLostHope = makeArticle({
  slug: "lost-hope-after-depression-treatments",
  title:
    "Lost Hope After Depression Treatments? There Are Options | Neuro Wellness",
  description:
    "If you've lost hope after trying several depression treatments, there are options you may not have tried. Learn about TMS, SAINT®, and SPRAVATO®.",
  canonical: "/learn/lost-hope-after-depression-treatments",
  h1: "I've Lost Hope After Trying Several Depression Treatments",
  breadcrumbLabel: "Lost Hope After Treatments",
  shortAnswer:
    "If you've lost hope after trying several depression treatments, your experience is valid and common. Many people who eventually found relief had to try multiple treatments first. Treatments like TMS, SAINT® accelerated TMS, and SPRAVATO® (esketamine) work through different mechanisms than antidepressants and are specifically indicated for depression that hasn't responded to medication. A psychiatric evaluation can review your history and identify options you haven't tried. If you are having thoughts of self-harm or suicide, please call or text 988 (Suicide & Crisis Lifeline) now — you don't have to go through this alone.",
  keyTakeaways: [
    "Losing hope after multiple treatments is common and valid.",
    "TMS, SAINT®, and SPRAVATO® work differently than medication.",
    "Many people find relief after trying several treatments.",
    "A psychiatric evaluation identifies options you haven't tried.",
    "If having thoughts of self-harm, call or text 988 now.",
  ],
  sections: [
    {
      heading: "Your Experience Is Valid",
      paragraphs: [
        "Losing hope after trying multiple depression treatments without adequate relief is a common and deeply understandable experience. It does not mean your depression is untreatable — it means the right treatment hasn't been found yet. Many people who eventually found relief had to try several treatments, including treatments that work through entirely different mechanisms than medication.",
      ],
    },
    {
      heading: "Treatments You May Not Have Tried",
      paragraphs: [
        "If antidepressants and therapy haven't provided adequate relief, these treatments work through different mechanisms:",
      ],
      list: [
        "TMS — FDA-cleared magnetic brain stimulation, no medication",
        "SAINT® — FDA-cleared accelerated 5-day fMRI-guided TMS",
        "SPRAVATO® — FDA-approved esketamine, different mechanism than standard antidepressants",
        "ECT — for severe or psychotic depression",
      ],
    },
    {
      heading: "If You're Having Thoughts of Self-Harm",
      paragraphs: [
        "If you are having thoughts of harming yourself or feeling that you'd be better off dead, please reach out now. Call or text 988 (Suicide & Crisis Lifeline) in the U.S., or go to your nearest emergency room. You don't have to go through this alone. These thoughts are a symptom of depression, not a reflection of your worth or your future. Effective treatments exist, and a psychiatric evaluation can help you find the right one.",
      ],
    },
  ],
  faqs: [
    {
      question:
        "I've lost hope after trying several depression treatments. What can I do?",
      answer:
        "Treatments like TMS, SAINT®, and SPRAVATO® work through different mechanisms than medication and are indicated for treatment-resistant depression. A psychiatric evaluation can identify options you haven't tried. If having thoughts of self-harm, call or text 988 now.",
    },
    {
      question: "Is there hope after multiple depression treatments fail?",
      answer:
        "Yes. Many people find relief after trying multiple treatments. TMS, SAINT®, SPRAVATO®, and ECT work through different mechanisms and were developed for treatment-resistant depression. A psychiatric evaluation can identify the right next step.",
    },
    {
      question: "What if I'm having thoughts of giving up?",
      answer:
        "Please call or text 988 (Suicide & Crisis Lifeline) now, or go to your nearest emergency room. These thoughts are a symptom of depression, not a reflection of your future. Effective treatments exist that you may not have tried. You don't have to go through this alone.",
    },
  ],
  sources: [
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],
  relatedLinks: notWorkingRelated,
});

export const pageDepressionKeepsComingBack = makeArticle({
  slug: "why-does-depression-keep-coming-back",
  title: "Why Does My Depression Keep Coming Back? | Neuro Wellness",
  description:
    "Why depression keeps returning: recurrent depression, treatment options, and what to discuss with your psychiatrist. Learn about TMS and SAINT®.",
  canonical: "/learn/why-does-depression-keep-coming-back",
  h1: "Why Does My Depression Keep Coming Back?",
  breadcrumbLabel: "Depression Keeps Coming Back",
  shortAnswer:
    "Depression that keeps returning is called recurrent depression. It's common, and it doesn't mean treatment has failed permanently. Recurrence can happen because the underlying brain circuits involved in mood regulation remain vulnerable, because treatment was stopped too early, or because maintenance treatment wasn't in place. Treatments like TMS and SAINT® target these brain circuits directly through magnetic stimulation. A psychiatric evaluation can help understand why depression keeps returning and develop a plan to prevent future episodes. If you are having thoughts of self-harm, call or text 988.",
  keyTakeaways: [
    "Recurrent depression is common and treatable.",
    "Recurrence may relate to brain circuit vulnerability.",
    "Treatment may have been stopped too early.",
    "Maintenance treatment can help prevent recurrence.",
    "TMS and SAINT® target mood circuits directly.",
  ],
  sections: [
    {
      heading: "Why Depression Recurs",
      paragraphs: [
        "Depression that keeps coming back is called recurrent depression. Several factors can contribute to recurrence:",
      ],
      list: [
        "Brain circuits involved in mood regulation remain vulnerable",
        "Treatment was stopped too early before full remission",
        "No maintenance treatment plan was in place",
        "Underlying conditions (bipolar disorder, anxiety, substance use) are untreated",
        "Stress, life events, or medical conditions trigger new episodes",
      ],
    },
    {
      heading: "What This Means for Treatment",
      paragraphs: [
        "If depression keeps returning, a psychiatric evaluation can help identify why and develop a plan. This may include maintenance medication, ongoing psychotherapy, or treatments like TMS and SAINT® that target mood-regulating brain circuits directly. The goal is not just to treat the current episode but to reduce the risk of future ones.",
      ],
    },
    {
      heading: "TMS and SAINT® for Recurrent Depression",
      paragraphs: [
        "TMS and SAINT® stimulate the prefrontal cortex — a brain region involved in mood regulation. For people whose depression keeps returning despite medication, these treatments offer a different approach. TMS is FDA-cleared for depression that hasn't responded to medication. SAINT® is an accelerated 5-day version. Neither is a guaranteed cure, but both work through mechanisms that differ from medication.",
      ],
    },
  ],
  faqs: [
    {
      question: "Why does my depression keep coming back?",
      answer:
        "Recurrent depression is common. It may relate to brain circuit vulnerability, treatment stopped too early, lack of maintenance treatment, or untreated underlying conditions. A psychiatric evaluation can identify why and develop a prevention plan.",
    },
    {
      question: "Can TMS help if my depression keeps returning?",
      answer:
        "TMS may help for recurrent depression that hasn't responded adequately to medication. It targets mood-regulating brain circuits directly. SAINT® is an accelerated 5-day version. Neither is a guaranteed cure, but both work through different mechanisms than medication.",
    },
    {
      question: "What should I do if my depression keeps coming back?",
      answer:
        "See a psychiatrist for a thorough evaluation. They can review your treatment history, identify why depression recurs, and develop a maintenance plan. This may include medication adjustments, therapy, or treatments like TMS and SAINT®.",
    },
  ],
  sources: [
    {
      citation:
        "American Psychiatric Association. Practice Guideline for the Treatment of Patients with Major Depressive Disorder.",
      url: "https://psychiatryonline.org/",
    },
  ],
  relatedLinks: notWorkingRelated,
});

export const pageDepressedForYears = makeArticle({
  slug: "depressed-for-years-what-else-can-i-try",
  title: "Depressed for Years — What Else Can I Try? | Neuro Wellness",
  description:
    "If you've been depressed for years, there are treatments you may not have tried. Learn about TMS, SAINT®, SPRAVATO®, and options for chronic depression.",
  canonical: "/learn/depressed-for-years-what-else-can-i-try",
  h1: "I've Been Depressed for Years — What Else Can I Try?",
  breadcrumbLabel: "Depressed for Years",
  shortAnswer:
    "If you've been depressed for years, there are treatments you may not have tried. Chronic depression (persistent depressive disorder or long-standing major depressive disorder) can be difficult to treat, but treatments like TMS, SAINT® accelerated TMS, SPRAVATO® (esketamine), and ECT work through different mechanisms than antidepressants and may help. A thorough psychiatric evaluation is essential — it can identify whether past treatments were adequate, whether an underlying condition has been missed, and which treatment may be appropriate. If you are having thoughts of self-harm, call or text 988.",
  keyTakeaways: [
    "Chronic depression is difficult but treatable.",
    "TMS, SAINT®, SPRAVATO®, and ECT work differently than medication.",
    "A thorough evaluation is essential.",
    "Past treatments may not have been adequate.",
    "If having thoughts of self-harm, call 988.",
  ],
  sections: [
    {
      heading: "Chronic Depression Is Difficult but Treatable",
      paragraphs: [
        "Being depressed for years is exhausting and discouraging, but it doesn't mean your depression is untreatable. Chronic depression — whether persistent depressive disorder (dysthymia) or long-standing major depressive disorder — can be harder to treat, but treatments that work through different mechanisms than medication may help when antidepressants haven't.",
      ],
    },
    {
      heading: "Treatments to Consider",
      paragraphs: [
        "If you've been depressed for years and medication hasn't provided adequate relief, these treatments work through different mechanisms:",
      ],
      list: [
        "TMS — FDA-cleared magnetic brain stimulation",
        "SAINT® — FDA-cleared accelerated 5-day fMRI-guided TMS",
        "SPRAVATO® — FDA-approved esketamine nasal spray",
        "ECT — for severe, chronic, or psychotic depression",
        "Intensive psychotherapy (CBT, DBT, interpersonal therapy)",
      ],
    },
    {
      heading: "The Importance of a Fresh Evaluation",
      paragraphs: [
        "If you've been depressed for years, a fresh psychiatric evaluation is essential. A new clinician may identify factors that previous evaluations missed — inadequate past medication trials, untreated underlying conditions (bipolar disorder, thyroid issues, substance use), or treatment approaches you haven't tried. Don't assume that because past treatments didn't work, nothing will. If you are having thoughts of self-harm, call or text 988 now.",
      ],
    },
  ],
  faqs: [
    {
      question: "I've been depressed for years. What else can I try?",
      answer:
        "Treatments like TMS, SAINT®, SPRAVATO®, and ECT work through different mechanisms than medication and may help. A fresh psychiatric evaluation can identify whether past treatments were adequate and which option may be appropriate. If having thoughts of self-harm, call 988.",
    },
    {
      question: "Can chronic depression be treated?",
      answer:
        "Yes. Chronic depression can be harder to treat, but treatments that work through different mechanisms — TMS, SAINT®, SPRAVATO®, ECT — may help when medication hasn't. A thorough psychiatric evaluation is the first step.",
    },
    {
      question: "What if I've tried everything for my depression?",
      answer:
        "A fresh evaluation may reveal that not everything has been adequately tried. Past medication trials may not have been adequate, or an underlying condition may have been missed. Treatments like TMS and SAINT® work through different mechanisms and may help.",
    },
  ],
  sources: [
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],
  relatedLinks: notWorkingRelated,
});

// NOTE: pageDepressionWontGoAway is exported from learnDataDepressionWontGoAway.tsx
// with slug "depression-wont-go-away-even-with-medication" to avoid duplicate slug conflicts.
// Do not re-declare it here.

export const pageDontWantToChangeAntidepressants = makeArticle({
  slug: "dont-want-to-keep-changing-antidepressants",
  title: "Don't Want to Keep Changing Antidepressants? | Neuro Wellness",
  description:
    "If you don't want to keep trying different antidepressants, there are non-medication options. Learn about TMS, SAINT®, and SPRAVATO®.",
  canonical: "/learn/dont-want-to-keep-changing-antidepressants",
  h1: "I Don't Want to Keep Changing Antidepressants",
  breadcrumbLabel: "Don't Want to Change Antidepressants",
  shortAnswer:
    "If you're tired of trying different antidepressants without adequate relief, there are non-medication treatment options. TMS and SAINT® are FDA-cleared brain stimulation treatments that don't involve medication — they work through magnetic pulses, not chemicals. SPRAVATO® (esketamine) is an FDA-approved nasal spray that works through a different mechanism than standard antidepressants. A psychiatric evaluation can help determine whether these options may be appropriate for you. Do not stop current medication without consulting your prescriber.",
  keyTakeaways: [
    "TMS and SAINT® are non-medication brain stimulation treatments.",
    "They work through magnetic pulses, not chemicals.",
    "SPRAVATO® works through a different mechanism than standard antidepressants.",
    "A psychiatric evaluation determines appropriateness.",
    "Do not stop medication without consulting your prescriber.",
  ],
  sections: [
    {
      heading: "Non-Medication Treatment Options",
      paragraphs: [
        "If you don't want to keep trying different antidepressants, treatments that don't involve medication may be appropriate:",
      ],
      list: [
        "TMS — FDA-cleared, non-invasive magnetic brain stimulation, no medication",
        "SAINT® — FDA-cleared, accelerated 5-day fMRI-guided TMS, no medication",
        "SPRAVATO® — FDA-approved esketamine nasal spray, different mechanism than standard antidepressants",
        "Psychotherapy — CBT, DBT, interpersonal therapy",
      ],
    },
    {
      heading: "How TMS and SAINT® Work Without Medication",
      paragraphs: [
        "TMS and SAINT® use magnetic pulses to stimulate specific brain regions involved in mood regulation. They introduce no chemical into the body and don't cause systemic side effects like weight gain, sexual dysfunction, or GI issues. Because they work through a different mechanism than medication, they may help when antidepressants haven't — without requiring you to try yet another medication.",
      ],
    },
    {
      heading: "Important: Don't Stop Medication on Your Own",
      paragraphs: [
        "If you want to reduce or stop medication, talk to your prescriber. Stopping abruptly can cause discontinuation symptoms and increase relapse risk. Your prescriber can plan a gradual taper and decide timing relative to any new treatment. TMS and SAINT® can be delivered alongside medication — combining approaches is often more effective than any single one.",
      ],
    },
  ],
  faqs: [
    {
      question:
        "I don't want to keep changing antidepressants. What are my options?",
      answer:
        "TMS and SAINT® are FDA-cleared brain stimulation treatments that don't involve medication. SPRAVATO® works through a different mechanism than standard antidepressants. A psychiatric evaluation can determine if these may be appropriate. Don't stop medication without consulting your prescriber.",
    },
    {
      question:
        "Are there depression treatments that don't involve medication?",
      answer:
        "Yes. TMS and SAINT® are FDA-cleared magnetic brain stimulation treatments that introduce no chemical into the body. They work through a different mechanism than medication and don't cause systemic side effects like weight gain or sexual dysfunction.",
    },
    {
      question: "Can I stop my antidepressant if I start TMS?",
      answer:
        "Not on your own. Stopping abruptly can cause discontinuation symptoms and increase relapse risk. Talk to your prescriber about a gradual taper. TMS can be delivered alongside medication — combining approaches is often more effective.",
    },
  ],
  sources: [
    {
      citation:
        "O'Reardon JP, et al. Efficacy and safety of transcranial magnetic stimulation in the acute treatment of major depression. Biol Psychiatry. 2007;62(11):1208-1216.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17573044/",
    },
  ],
  relatedLinks: notWorkingRelated,
});

export const pageOptionsIfTherapyDoesntHelp = makeArticle({
  slug: "options-if-therapy-doesnt-help-depression",
  title:
    "What Are My Options If Therapy Doesn't Help My Depression? | Neuro Wellness",
  description:
    "If therapy isn't helping your depression, there are other options. Learn about medication, TMS, SAINT®, SPRAVATO®, and what to discuss with your clinician.",
  canonical: "/learn/options-if-therapy-doesnt-help-depression",
  h1: "What Are My Options If Therapy Doesn't Help My Depression?",
  breadcrumbLabel: "Options If Therapy Doesn't Help",
  shortAnswer:
    "If therapy alone hasn't provided adequate relief for your depression, there are additional options. Depression is often treated with a combination of psychotherapy and medication, and adding medication or a brain stimulation treatment may help. TMS and SAINT® are FDA-cleared magnetic brain stimulation treatments that work through a different mechanism than therapy or medication. SPRAVATO® (esketamine) is an FDA-approved nasal spray for treatment-resistant depression. A psychiatric evaluation can help determine which combination of treatments may be appropriate. Therapy often remains valuable alongside other treatments.",
  keyTakeaways: [
    "Therapy alone may not be enough for moderate-to-severe depression.",
    "Adding medication or brain stimulation may help.",
    "TMS and SAINT® work through a different mechanism than therapy.",
    "Therapy often remains valuable alongside other treatments.",
    "A psychiatric evaluation guides the treatment plan.",
  ],
  sections: [
    {
      heading: "Why Therapy Alone May Not Be Enough",
      paragraphs: [
        "Therapy can be ineffective for several reasons: the specific therapy type may not match your needs (CBT, psychodynamic, interpersonal, and behavioral activation all work differently), the therapist-client fit may not be right, therapy alone may be insufficient for moderate-to-severe depression, or an underlying condition (like bipolar disorder or a medical issue) may need different treatment. For moderate-to-severe depression, combining therapy with medication or other treatments is often more effective than therapy alone.",
      ],
    },
    {
      heading: "Options Beyond Therapy Alone",
      paragraphs: [
        "If therapy hasn't provided adequate relief, additional options include:",
      ],
      list: [
        "Antidepressant medication — often combined with therapy",
        "TMS — FDA-cleared magnetic brain stimulation",
        "SAINT® — FDA-cleared accelerated 5-day fMRI-guided TMS",
        "SPRAVATO® — FDA-approved esketamine nasal spray",
        "Combined treatment — therapy plus medication and/or brain stimulation",
      ],
    },
    {
      heading: "Therapy Still Has Value",
      paragraphs: [
        "Even if therapy alone hasn't been enough, therapy often remains valuable alongside other treatments. Combined treatment — therapy plus medication and/or brain stimulation — is often more effective than any single approach. A psychiatric evaluation can help determine the right combination for your situation.",
      ],
    },
  ],
  faqs: [
    {
      question: "What are my options if therapy doesn't help my depression?",
      answer:
        "Options include antidepressant medication, TMS, SAINT®, SPRAVATO®, or combined treatment. TMS and SAINT® work through a different mechanism than therapy. Therapy often remains valuable alongside other treatments. A psychiatric evaluation can guide the plan.",
    },
    {
      question: "Can TMS help if therapy hasn't worked?",
      answer:
        "Yes. TMS is FDA-cleared for depression that hasn't responded to medication. It works through magnetic brain stimulation — a different mechanism than therapy. If therapy alone hasn't provided adequate relief, TMS may be appropriate. Therapy often continues alongside TMS.",
    },
    {
      question: "Should I stop therapy if I start TMS or medication?",
      answer:
        "Not necessarily. Combined treatment — therapy plus medication and/or brain stimulation — is often more effective than any single approach. Discuss with your clinician whether to continue, adjust, or pause therapy. Therapy remains valuable for many patients.",
    },
  ],
  sources: [
    {
      citation:
        "Cuijpers P, et al. The effects of psychotherapy for adult depression are overestimated: a meta-analysis of study quality and funnel plot asymmetry.",
      url: "https://pubmed.ncbi.nlm.nih.gov/23395197/",
    },
  ],
  relatedLinks: notWorkingRelated,
});

export const pageDepressionImpossibleToTreat = makeArticle({
  slug: "depression-feels-impossible-to-treat",
  title:
    "Depression Feels Impossible to Treat? There Are Options | Neuro Wellness",
  description:
    "When depression feels impossible to treat, there are options you may not have tried. Learn about TMS, SAINT®, SPRAVATO®, and ECT for severe depression.",
  canonical: "/learn/depression-feels-impossible-to-treat",
  h1: "What Options Exist When Depression Feels Impossible to Treat?",
  breadcrumbLabel: "Depression Feels Impossible",
  shortAnswer:
    "When depression feels impossible to treat, there are still options. Severe, treatment-resistant depression can be difficult to manage, but treatments like TMS, SAINT® accelerated TMS, SPRAVATO® (esketamine), and ECT work through different mechanisms than antidepressants and were developed specifically for difficult-to-treat cases. ECT has the highest remission rates for severe or psychotic depression. A thorough psychiatric evaluation — possibly inpatient — can identify the right approach. If you are having thoughts of self-harm or suicide, call or text 988 now, or go to your nearest emergency room.",
  keyTakeaways: [
    "Severe depression is difficult but treatable.",
    "TMS, SAINT®, SPRAVATO®, and ECT work through different mechanisms.",
    "ECT has the highest remission rates for severe depression.",
    "A thorough evaluation identifies the right approach.",
    "If having thoughts of self-harm, call 988 now.",
  ],
  sections: [
    {
      heading: "Difficult Does Not Mean Impossible",
      paragraphs: [
        "When depression feels impossible to treat, it's usually because multiple treatments haven't worked. This is the definition of treatment-resistant depression, and it affects roughly one-third of people treated for depression. But 'resistant' doesn't mean 'untreatable' — it means the right treatment hasn't been found yet. Treatments that work through different mechanisms than medication may help when antidepressants haven't.",
      ],
    },
    {
      heading: "Options for Difficult-to-Treat Depression",
      paragraphs: [
        "When standard treatments haven't worked, these options work through different mechanisms:",
      ],
      list: [
        "TMS — FDA-cleared magnetic brain stimulation",
        "SAINT® — FDA-cleared accelerated 5-day fMRI-guided TMS",
        "SPRAVATO® — FDA-approved esketamine nasal spray",
        "ECT — highest remission rates for severe or psychotic depression",
        "Combined treatment — multiple approaches together",
      ],
    },
    {
      heading: "If You're in Crisis",
      paragraphs: [
        "If you are having thoughts of harming yourself or feeling that you'd be better off dead, please reach out now. Call or text 988 (Suicide & Crisis Lifeline) in the U.S., or go to your nearest emergency room. You don't have to go through this alone. These thoughts are a symptom of depression, not a reflection of your worth or your future. Effective treatments exist that you may not have tried.",
      ],
    },
  ],
  faqs: [
    {
      question: "What options exist when depression feels impossible to treat?",
      answer:
        "TMS, SAINT®, SPRAVATO®, and ECT work through different mechanisms than medication and were developed for difficult-to-treat depression. ECT has the highest remission rates for severe depression. A thorough evaluation can identify the right approach. If in crisis, call 988.",
    },
    {
      question: "Is severe depression ever truly untreatable?",
      answer:
        "Rarely. Even severe, treatment-resistant depression usually responds to some treatment. ECT has high remission rates for severe depression. Newer treatments like SAINT® and SPRAVATO® offer additional options. A thorough evaluation — possibly inpatient — can identify the right approach.",
    },
    {
      question: "What if I've tried everything and nothing works?",
      answer:
        "A fresh evaluation may reveal that not everything has been adequately tried. Past medication trials may not have been adequate, or an underlying condition may have been missed. Treatments like TMS, SAINT®, SPRAVATO®, and ECT work through different mechanisms. If in crisis, call 988.",
    },
  ],
  sources: [
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],
  relatedLinks: notWorkingRelated,
});
